import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function SentinelGuardList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [guards, setGuards] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, guardsRes, typesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(`${API_BASE_URL}/incidents/sentinel-guards`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(`${API_BASE_URL}/incidents/types`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!guardsRes.ok) throw new Error("Failed to fetch sentinel guards");
      if (!typesRes.ok) throw new Error("Failed to fetch incident types");

      const dashboardResult = await dashboardRes.json();
      const guardsResult = await guardsRes.json();
      const typesResult = await typesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const guardList = Array.isArray(guardsResult?.data) ? guardsResult.data : [];
      const typeList = Array.isArray(typesResult?.data) ? typesResult.data : [];

      setGuards(guardList);
      setIncidentTypes(typeList);

      await fetchAllGuardPermissions(guardList, token);
    } catch (error) {
      console.error("Sentinel guard list page error:", error);
      toast.error(error.message || "Failed to load sentinel guard list.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGuardPermissions = async (guardList, token) => {
    try {
      const permissionMap = {};

      await Promise.all(
        guardList.map(async (guard) => {
          if (!guard.member_id) return;

          const res = await fetch(
            `${API_BASE_URL}/sentinel_permission/${encodeURIComponent(guard.member_id)}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!res.ok) {
            permissionMap[guard.member_id] = {};
            return;
          }

          const data = await res.json();
          const selectedIds = Array.isArray(data?.selectedIds) ? data.selectedIds : [];

          permissionMap[guard.member_id] = {};

          selectedIds.forEach((id) => {
            permissionMap[guard.member_id][Number(id)] = true;
          });
        })
      );

      setPermissions(permissionMap);
    } catch (error) {
      console.error("Fetch sentinel permission error:", error);
      toast.error("Failed to load guard permissions.");
      setPermissions({});
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredGuards = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return guards;

    return guards.filter((guard) => {
      const memberId = String(guard.member_id || "").toLowerCase();
      const guardId = String(guard.guard_id || "").toLowerCase();
      const name = String(guard.name || "").toLowerCase();
      const username = String(guard.username || "").toLowerCase();
      const role = String(guard.role || "").toLowerCase();
      const designation = String(guard.designation || "").toLowerCase();

      return (
        memberId.includes(keyword) ||
        guardId.includes(keyword) ||
        name.includes(keyword) ||
        username.includes(keyword) ||
        role.includes(keyword) ||
        designation.includes(keyword)
      );
    });
  }, [guards, search]);

  const togglePermission = (memberId, typeId) => {
    setPermissions((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || {}),
        [typeId]: !prev?.[memberId]?.[typeId],
      },
    }));
  };

  const backToIncidents = () => navigate("/add_incidents");

  const handleSavePermissions = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      await Promise.all(
        guards.map(async (guard) => {
          if (!guard.member_id) return;

          const typeIds = incidentTypes
            .filter((type) => permissions?.[guard.member_id]?.[type.id])
            .map((type) => Number(type.id))
            .filter((id) => Number.isFinite(id));

          const res = await fetch(
            `${API_BASE_URL}/sentinel_permission/${encodeURIComponent(guard.member_id)}/save`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                type_ids: typeIds,
              }),
            }
          );

          const data = await res.json();

          if (!res.ok || data.success === false) {
            throw new Error(data.message || `Failed to save permission for ${guard.member_id}`);
          }
        })
      );

      toast.success("Sentinel guard permissions saved successfully.");
      setTimeout(() => {
        navigate("/add_incidents");
      }, 1000);
    } catch (error) {
      console.error("Save sentinel permission error:", error);
      toast.error(error.message || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel guard list...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="incidents"
      activeMenu="incidents"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Guard List
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Add incident type permissions for sentinel guards
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel Guard Permission Management
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Select which incident types each sentinel guard can access
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={backToIncidents}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSavePermissions}
                disabled={saving}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving ? "Saving..." : "Save Permission"}
              </button>
            </div>
          </div>
        </div>

        {/* Search and Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by member ID, guard ID, name, username, role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[1200px] border-collapse text-[13px] font-['Inter',system-ui,sans-serif]">
              <thead>
                <tr style={{ background: "var(--btn-bg)" }}>
                  {["Member ID", "Guard ID", "Guard", "Username", "Designation", "Status", "Incident Type Permission"].map((head) => (
                    <th key={head} className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredGuards.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-[var(--text-muted)]">
                      No sentinel guards found.
                    </td>
                  </tr>
                ) : (
                  filteredGuards.map((guard, index) => (
                    <tr
                      key={`${guard.member_id}-${guard.guard_id || index}`}
                      className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors"
                    >
                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <span className="text-[var(--accent)] font-extrabold">
                          {guard.member_id || "-"}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] text-[var(--text-primary)] align-middle">
                        {guard.guard_id || "-"}
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center font-extrabold shadow-[0_3px_12px_var(--accent-glow)]">
                            {String(guard.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="font-extrabold text-[var(--text-primary)]">
                            {guard.name || "-"}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] text-[var(--text-primary)] align-middle">
                        {guard.username || "-"}
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <span className="inline-flex px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]">
                          {guard.designation || guard.role || "Guard"}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <span
                          className={`inline-flex px-3 py-1.5 rounded-full text-[11.5px] font-extrabold ${
                            guard.is_active === true || guard.is_active === 1 || guard.is_active === "1"
                              ? "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
                              : "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
                          }`}
                        >
                          {guard.is_active === true || guard.is_active === 1 || guard.is_active === "1"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        {incidentTypes.length === 0 ? (
                          <span className="text-[var(--text-muted)] text-[12px]">No incident types found</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {incidentTypes.map((type) => {
                              const checked = !!permissions?.[guard.member_id]?.[type.id];

                              return (
                                <label
                                  key={`${guard.member_id}-${type.id}`}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-[11.5px] font-extrabold cursor-pointer transition-all ${
                                    checked
                                      ? "border-[var(--accent-border)] bg-[var(--accent-faint)] text-[var(--accent)]"
                                      : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePermission(guard.member_id, type.id)}
                                    className="w-4 h-4 accent-[var(--accent)] cursor-pointer"
                                  />
                                  <span className="text-[15px]">{type.icon_value || "⚠️"}</span>
                                  <span>{type.label || type.type_key}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}